import React from "react";
import "./CircularProgressBar.css";

interface CircularProgressBarProps {
  // NOTE: typed as string per the original propTypes, even though sqSize and
  // strokeWidth are used in arithmetic (see render) — a pre-existing quirk.
  // The numeric arithmetic relies on JS coercing these strings at runtime, so
  // each arithmetic use casts via `as unknown as number` (type-only, no
  // behavior change) to type the quirk faithfully without altering output.
  percentage?: string;
  sqSize?: string;
  strokeWidth?: string;
}

interface CircularProgressBarState {
  // Seeded from props.percentage (a string) in the constructor and updated
  // from props.percentage in componentDidUpdate; kept as string to match.
  percentage?: string;
}

class CircularProgressBar extends React.Component<
  CircularProgressBarProps,
  CircularProgressBarState
> {
  constructor(props: CircularProgressBarProps) {
    super(props);
    this.state = {
      percentage: this.props.percentage,
    };
  }

  componentDidUpdate(prevProps: CircularProgressBarProps) {
    if (prevProps.percentage !== this.props.percentage) {
      this.setState({
        percentage: this.props.percentage,
      });
    }
  }

  render() {
    // Size of the enclosing square
    const sqSize = this.props.sqSize;
    // SVG centers the stroke width on the radius, subtract out so circle fits in square
    const radius =
      ((this.props.sqSize as unknown as number) -
        (this.props.strokeWidth as unknown as number)) /
      2;
    // Enclose cicle in a circumscribing square
    const viewBox = `0 0 ${sqSize} ${sqSize}`;
    // Arc length at 100% coverage is the circle circumference
    const dashArray = radius * Math.PI * 2;
    // Scale 100% coverage overlay with the actual percent
    const dashOffset =
      dashArray -
      (dashArray * (this.state.percentage as unknown as number)) / 100;

    return (
      <svg
        width={this.props.sqSize}
        height={this.props.sqSize}
        viewBox={viewBox}
      >
        <defs>
          <pattern
            id="img2"
            patternUnits="userSpaceOnUse"
            width={this.props.sqSize}
            height={this.props.sqSize}
          >
            <image
              href="/assets/gradient-bg.png"
              // viewBox is a string; .x/.y are always undefined here — a
              // pre-existing bug, preserved verbatim (cast to keep behavior).
              x={(viewBox as unknown as { x?: number }).x}
              y={(viewBox as unknown as { y?: number }).y}
              width={this.props.sqSize}
              height={this.props.sqSize}
            />
          </pattern>
        </defs>
        <circle
          className="circle-background"
          cx={(this.props.sqSize as unknown as number) / 2}
          cy={(this.props.sqSize as unknown as number) / 2}
          r={radius}
          strokeWidth={`${this.props.strokeWidth}px`}
        />
        <circle
          className="circle-progress"
          fill="none"
          stroke="url(#img2)"
          cx={(this.props.sqSize as unknown as number) / 2}
          cy={(this.props.sqSize as unknown as number) / 2}
          r={radius}
          strokeWidth={`${this.props.strokeWidth}px`}
          // Start progress marker at 12 O'Clock
          transform={`rotate(-90 ${
            (this.props.sqSize as unknown as number) / 2
          } ${(this.props.sqSize as unknown as number) / 2})`}
          style={{
            strokeDasharray: dashArray,
            strokeDashoffset: dashOffset,
          }}
        />
        <text
          className="circle-text"
          x="50%"
          y="50%"
          dy=".3em"
          textAnchor="middle"
        >
          {`${this.state.percentage}%`}
        </text>
      </svg>
    );
  }
}

export default CircularProgressBar;
